/**
 * Главная админ-страница: сводка по users / chats / cache / usage.
 * Все цифры — single GET /api/admin/overview.
 */

import { Alert, Card, Grid, Group, Loader, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { fetchAdminOverview, type AdminOverview } from '../../lib/api-admin';
import { fmtBytes, fmtNum, fmtUsd } from './format';

export function AdminOverviewPage(): JSX.Element {
    const q = useQuery({ queryKey: ['admin-overview'], queryFn: fetchAdminOverview, retry: false });
    return (
        <Stack gap="md">
            <Title order={2}>Обзор</Title>
            {q.isLoading && <Loader />}
            {q.error && <Alert color="red">{(q.error as Error).message}</Alert>}
            {q.data && <OverviewView data={q.data} />}
        </Stack>
    );
}

function OverviewView({ data }: { data: AdminOverview }): JSX.Element {
    return (
        <Stack gap="lg">
            <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
                <StatCard label="Пользователей" value={fmtNum(data.users.total)} sub={`из них админов: ${data.users.admins}`} />
                <StatCard label="Чат-сессий" value={fmtNum(data.chat.sessionsTotal)} />
                <StatCard label="Snapshot v2" value={fmtNum(data.storage.snapshotsV2.count)} sub={fmtBytes(data.storage.snapshotsV2.bytes)} />
                <StatCard label="Repo checkouts" value={fmtNum(data.storage.repoCheckouts.count)} sub={fmtBytes(data.storage.repoCheckouts.bytes)} />
                <StatCard label="Diff-cache" value={fmtNum(data.storage.diffCache.count)} sub={fmtBytes(data.storage.diffCache.bytes)} />
                <StatCard label="LLM-cache" value={fmtNum(data.storage.llmCache.count)} sub={fmtBytes(data.storage.llmCache.bytes)} />
            </SimpleGrid>

            <Title order={4}>Расход LLM</Title>
            <Grid gutter="md">
                <Grid.Col span={{ base: 12, sm: 4 }}><UsagePeriodCard title="За 24 часа" totals={data.usage.last24h} /></Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4 }}><UsagePeriodCard title="За 7 дней"  totals={data.usage.last7d}  /></Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4 }}><UsagePeriodCard title="За 30 дней" totals={data.usage.last30d} /></Grid.Col>
            </Grid>
        </Stack>
    );
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }): JSX.Element {
    return (
        <Card withBorder padding="md">
            <Text fz="xs" c="dimmed" tt="uppercase">{label}</Text>
            <Text fz={{ base: 'lg', md: 'xl' }} fw={700}>{value}</Text>
            {sub && <Text fz="xs" c="dimmed" mt={4}>{sub}</Text>}
        </Card>
    );
}

function UsagePeriodCard({ title, totals }: { title: string; totals: AdminOverview['usage']['last24h'] }): JSX.Element {
    return (
        <Card withBorder padding="md">
            <Text fw={600} mb="xs">{title}</Text>
            <Group justify="space-between" mb={4}>
                <Text fz="sm" c="dimmed">Вызовов</Text>
                <Text fz="sm" fw={500}>{fmtNum(totals.calls)}</Text>
            </Group>
            <Group justify="space-between" mb={4}>
                <Text fz="sm" c="dimmed">Расход</Text>
                <Text fz="sm" fw={500}>{fmtUsd(totals.costUsd)}</Text>
            </Group>
            <Group justify="space-between" mb={4}>
                <Text fz="sm" c="dimmed">Input</Text>
                <Text fz="sm">{fmtNum(totals.inputTokens)}</Text>
            </Group>
            <Group justify="space-between">
                <Text fz="sm" c="dimmed">Output</Text>
                <Text fz="sm">{fmtNum(totals.outputTokens)}</Text>
            </Group>
        </Card>
    );
}
